/**
 * Fund-a-Feature Terms — legal terms specifically for the Fund-a-Feature
 * program. Separate from the main /terms page so this can be linked
 * directly inside the submission form (and updated independently).
 *
 * These are plain-English starter terms — the founder should run them past
 * counsel before going to GA, but they're enough to cover the basics:
 * pledges are not legally binding orders, refund policy, scoping rights,
 * IP ownership stays with Bloq, no exclusivity, no public attribution
 * without consent.
 */
import { Link } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";

export default function FundAFeatureTerms() {
  return (
    <div className="bg-[#fbfcfc] min-h-screen">
      <MarketingNav />
      <section className="bloq-section">
        <div className="bloq-container max-w-3xl">
          <p className="bloq-eyebrow">Legal</p>
          <h1 className="bloq-display text-4xl sm:text-5xl mt-3 mb-2">Fund a Feature — Terms</h1>
          <p className="text-sm text-[#82828c] mb-12">Last updated: February 21, 2026</p>

          <div className="prose prose-sm max-w-none text-[#1a1a1f] space-y-6">
            <Section title="1. What Fund a Feature is">
              <p>
                The Fund a Feature program lets you sponsor the development of a specific feature on
                the Bloq platform. By submitting a pledge through the form on the Fund a Feature
                page, you indicate your interest in contributing funds toward the build of a defined
                feature. These terms govern that relationship.
              </p>
            </Section>

            <Section title="2. Pledges are non-binding until confirmed">
              <p>
                Submitting a pledge is <strong>not a binding order</strong> and does not authorize a
                charge to your payment method. After we receive your submission, the Bloq team will
                review your idea and reply within <strong>three (3) business days</strong> with one of:
              </p>
              <ul className="list-disc pl-6">
                <li>A confirmed scope, timeline, and final cost — at which point you may choose to proceed.</li>
                <li>A request for clarification or refinement.</li>
                <li>A decline if the feature is infeasible, out of scope, or already planned for general release.</li>
              </ul>
              <p>You will only be charged after you have explicitly confirmed the scoped quote in writing.</p>
            </Section>

            <Section title="3. Refunds">
              <p>
                Once development has started, pledges are <strong>refundable on a prorated basis</strong>
                — covering any work not yet completed — for the first 14 days after kickoff. After 14
                days, pledges are non-refundable except where required by law. If we cannot deliver a
                feature we have accepted and started, we will refund the unspent portion of your pledge
                in full.
              </p>
            </Section>

            <Section title="4. Scoping rights and feasibility">
              <p>
                We reserve the right to <strong>decline any request</strong> at our discretion,
                including ideas we determine are technically infeasible, conflict with our product
                direction, or duplicate work already on the public roadmap. We also reserve the right
                to modify scope based on technical constraints we discover during the build, with
                your prior approval before any change that materially affects cost or timeline.
              </p>
            </Section>

            <Section title="5. Ownership and intellectual property">
              <p>
                All features built under the Fund a Feature program remain the
                <strong> exclusive intellectual property of Bloq, Inc.</strong>. Sponsoring a feature
                does <strong>not</strong> grant you ownership rights, exclusive access, source code,
                white-label rights, or veto power over future changes. Funded features will be
                available to all Bloq users on the appropriate plan tier.
              </p>
            </Section>

            <Section title="6. No public attribution without consent">
              <p>
                We will <strong>not</strong> publicly identify you or your organization as a Fund a
                Feature sponsor without your express written permission. If you want to be credited
                on our public roadmap or release notes, let us know — we're happy to do so when you
                ask.
              </p>
            </Section>

            <Section title="7. Roadmap priority, not custom development">
              <p>
                Funded features are added to our <strong>public roadmap</strong> and prioritized
                accordingly. They are not bespoke custom development for your account only. We
                generally aim to ship funded features within the quarter the pledge is confirmed,
                but timelines are estimates and not guarantees.
              </p>
            </Section>

            <Section title="8. No partnership, employment, or fiduciary relationship">
              <p>
                Funding a feature does <strong>not</strong> create any partnership, joint venture,
                employment, agency, or fiduciary relationship between you and Bloq, Inc.. You are a
                customer/sponsor; we are the developer.
              </p>
            </Section>

            <Section title="9. Changes to these terms">
              <p>
                We may update these terms from time to time. The "Last updated" date at the top of
                this page reflects the most recent change. Pledges accepted before a change will
                continue to be governed by the terms in effect at the time of confirmation.
              </p>
            </Section>

            <Section title="10. Governing law and disputes">
              <p>
                These terms are governed by the laws of the State of Colorado, USA, without regard
                to its conflict-of-laws principles. Any dispute arising from the Fund a Feature
                program will be resolved by binding arbitration in Denver, Colorado, except that
                either party may seek injunctive relief in a court of competent jurisdiction.
              </p>
            </Section>

            <Section title="11. Contact">
              <p>
                Questions about these terms? Email <a href="mailto:legal@bloq.video" className="text-[#005e7a] underline">legal@bloq.video</a> or
                reach us via the <Link to="/contact" className="text-[#005e7a] underline">contact form</Link>.
              </p>
            </Section>
          </div>

          <div className="mt-16 bloq-card p-7">
            <p className="text-sm text-[#4a4a52]">
              Ready to sponsor a feature?{" "}
              <Link to="/fund-a-feature" className="text-[#005e7a] underline font-medium">
                Submit your idea on the Fund a Feature page →
              </Link>
            </p>
          </div>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="bloq-headline text-xl mt-8 mb-2">{title}</h2>
      <div className="text-base text-[#4a4a52] leading-relaxed space-y-3">{children}</div>
    </div>
  );
}
